import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { Button, Input, InputNumber, message, Select, Space, Table, Tag, Switch } from 'antd';
import { SaveOutlined, EditOutlined, KeyOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useUserApi } from '../../../api/user/userApp';
import { TUserApp } from '../../../models/user/User';
import { TCodification } from '../../../models/referential/referential';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../../constant/codification';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import { CODIFICATION_ROLES } from '../../../constant/codification';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';
import type { GetProps } from 'antd';

type SearchProps = GetProps<typeof Input.Search>;

const { Search } = Input;

const UserAppList: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isReferentialLoaded, getCodificationById } = useReferential();
    const { getAllUserApps, createUserApp, updateUserApp, deleteUserApp, activateUserApp, deactivateUserApp, getOrganizationByUserappId } = useUserApi();
    const { isRoleExist } = useTilmiAuth();
    const canManageActivation = isRoleExist(CODIFICATION_ROLES.ACTIVATE_DEACTIVATE);
    const [userApps, setUserApps] = useState<TUserApp[]>([]);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [addingId, setAddingId] = useState<number>(0);
    const { getCodificationsByCodeType } = useReferential();
    const [userAppsFiltered, setUserAppsFiltered] = useState<TUserApp[]>([]);
    const userTypes: TCodification[] = getCodificationsByCodeType(CODIFICATION_TYPE.USER_TYPE);
    const [editingKey, setEditingKey] = useState<number | null>(null);
    const [editingUserApp, setEditingUserApp] = useState<TUserApp | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);
    const [isBulkDialogVisible, setIsBulkDialogVisible] = useState<boolean>(false);
    const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | null>(null);
    const [bulkLoading, setBulkLoading] = useState<boolean>(false);
    const [typeUsers, setTypeUsers] = useState<TCodification[]>([]);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>([]);
    const [organizations, setOrganizations] = useState<TUserApp[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [userOrgMap, setUserOrgMap] = useState<Record<number, number | null>>({});

    useEffect(() => {
        setIsLoading(true);
        getAllUserApps()
            .then((resp) => {
                const allUsers: TUserApp[] = resp.data;
                setUserApps(allUsers);

                const orgs = allUsers.filter((u) => {
                    if (u.business_info && u.business_info.name) return true;
                    const codif = getCodificationById(u.usertype_id);
                    return codif?.code === 'BUSINESS';
                });
                setOrganizations(orgs);

                recomputeFiltered(allUsers, searchValue, statusFilter, selectedUserTypes, selectedOrgId);
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Fonction pour initialiser les types d'utilisateurs en fonction des droits de l'utilisateur connecté
        const initializeUserTypes = async () => {
            try {
                // Récupérer les codifications des types d'utilisateur
                const types: TCodification[] = await getCodificationsByCodeType(CODIFICATION_TYPE.USER_TYPE);

                // Si les types existent, les ajouter à la liste
                if (types && Array.isArray(types)) {
                    setTypeUsers(types); // On met à jour directement avec le tableau
                }
            } catch (err: any) {
                message.error(err.response.data ?? err.message);
            }
        };
        initializeUserTypes();
        // eslint-disable-next-line
    }, [isReferentialLoaded]);


    const edit = (userapp: TUserApp) => {
        setEditingKey(userapp.id);
        setEditingUserApp({ ...userapp });
    };

    const handleAddUserApp = () => {
        const idRow = addingId - 1;
        const newUserApp: TUserApp = {
            id: idRow,
            login: '',
            max_session: -1,
            usertype_id: 0,
        };

        setAddingId(idRow);
        setEditingKey(idRow);
        setEditingUserApp(newUserApp);
        setUserApps((prev) => [newUserApp, ...prev]);
    };

    const save = (id: number) => {
        if (editingUserApp) {
            (editingUserApp.id > 0 ? updateUserApp(editingUserApp.id, editingUserApp) : createUserApp(editingUserApp))
                .then((resp) => {
                    const userappResult: TUserApp = resp.data;
                    const updatedUserApps = userApps.map((userapp) =>
                        userapp.id === id ? userappResult : userapp
                    );
                    setUserApps(updatedUserApps);
                    recomputeFiltered(updatedUserApps, searchValue, statusFilter, selectedUserTypes);
                    setEditingKey(null);
                    setEditingUserApp(null);
                    message.success(t('message.savedSuccessfully'));
                })
                .catch((err) => {
                    message.error(err.response.data ?? err.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const showDetails = (userapp: TUserApp) => {
        navigate('/setup/passwords', { state: { userapp } });
    };

    const cancel = () => {
        const idRow = editingUserApp?.id || 0;
        if (idRow <= 0) {
            setUserApps((prev) => [...prev ?? []].filter(e => e.id !== idRow));
        }
        setEditingKey(null);
        setEditingUserApp(null);
    };

    const confirmDelete = (id: number) => {
        setDeleteId(id);
        setIsDeleteDialogVisible(true);
    };

    const handleDelete = () => {
        if (deleteId == null) return;
        setDeleteLoading(true);
        deleteUserApp(deleteId)
            .then(() => {
                const updatedUserApps = userApps.filter((userapp) => userapp.id !== deleteId);
                setUserApps(updatedUserApps);
                recomputeFiltered(updatedUserApps, searchValue, statusFilter, selectedUserTypes);
                message.success(t('message.deletedSuccessfully'));
            })
            .catch((err) => {
                message.error(err.response.data ?? err.message);
            })
            .finally(() => {
                setDeleteLoading(false);
                setIsDeleteDialogVisible(false);
                setDeleteId(null);
            });
    };

    const toggleActif = async (user: TUserApp) => {
        try {
            const isActivating = user.actif === false;
            const apiCall = isActivating ? activateUserApp : deactivateUserApp;
            const { data } = await apiCall(user.id);
            const updatedUserApps = userApps.map((u) => (u.id === user.id ? data : u));
            setUserApps(updatedUserApps);
            recomputeFiltered(updatedUserApps, searchValue, statusFilter, selectedUserTypes);
            message.success(
                isActivating
                    ? `${user.login} ${t('admin.userActivation.messages.activated', 'has been activated')}`
                    : `${user.login} ${t('admin.userActivation.messages.deactivated', 'has been deactivated')}`
            );
        } catch (err: any) {
            message.error(err?.response?.data ?? err.message);
        }
    };

    const openBulkDialog = (action: 'activate' | 'deactivate') => {
        setBulkAction(action);
        setIsBulkDialogVisible(true);
    };

    const handleBulkApply = async () => {
        if (!bulkAction) return;
        setBulkLoading(true);
        try {
            const targets = userAppsFiltered.filter((u) =>
                bulkAction === 'activate' ? u.actif === false : u.actif !== false
            );
            if (targets.length === 0) {
                setIsBulkDialogVisible(false);
                setBulkLoading(false);
                message.info(t('admin.userActivation.messages.noChanges', 'Aucun changement à appliquer'));
                return;
            }
            const apiCall = bulkAction === 'activate' ? activateUserApp : deactivateUserApp;
            const results = await Promise.allSettled(targets.map((u) => apiCall(u.id)));

            const updatedMap = new Map<number, TUserApp>();
            let ok = 0, ko = 0;
            results.forEach((r, idx) => {
                const id = targets[idx].id;
                if (r.status === 'fulfilled') {
                    const data = (r.value as any).data as TUserApp;
                    if (data) {
                        updatedMap.set(id, data);
                        ok++;
                    }
                } else {
                    ko++;
                }
            });

            if (ok > 0) {
                const updated = userApps.map((u) => updatedMap.get(u.id) ?? u);
                setUserApps(updated);
                recomputeFiltered(updated, searchValue, statusFilter, selectedUserTypes, selectedOrgId);
            }
            if (ko > 0) {
                message.warning(t('admin.userActivation.messages.partial', `${ok} réussi(s), ${ko} échec(s)`));
            } else {
                message.success(t('message.savedSuccessfully'));
            }
        } catch (err: any) {
            message.error(err?.response?.data ?? err.message);
        } finally {
            setBulkLoading(false);
            setIsBulkDialogVisible(false);
            setBulkAction(null);
        }
    };

    const recomputeFiltered = (
        nextUsers: TUserApp[] = userApps,
        nextSearch: string = searchValue,
        nextStatus: 'all' | 'active' | 'inactive' = statusFilter,
        nextUserTypes: number[] = selectedUserTypes,
        nextOrgId: number | null = selectedOrgId,
    ) => {
        const search = nextSearch.trim().toLowerCase();
        const res = nextUsers.filter((u) => {
            // Search filter
            const matchesSearch =
                search === '' ||
                u.login?.toLocaleLowerCase().includes(search) ||
                (u.business_info && u.business_info.name?.toLocaleLowerCase().includes(search)) ||
                (u.personal_info && u.personal_info.firstname?.toLocaleLowerCase().includes(search)) ||
                (u.personal_info && u.personal_info.lastname?.toLocaleLowerCase().includes(search));

            // Status filter
            const isActive = u.actif !== false;
            const matchesStatus =
                nextStatus === 'all' ||
                (nextStatus === 'active' && isActive) ||
                (nextStatus === 'inactive' && !isActive);

            // User type filter
            const matchesUserType =
                nextUserTypes.length === 0 || nextUserTypes.includes(u.usertype_id);

            // Organization filter: only include direct children of the selected
            // organization. Do not include the organization itself.
            let matchesOrg = true;
            if (nextOrgId && nextOrgId > 0) {
                if (u.id === nextOrgId) {
                    matchesOrg = false;
                } else {
                    const rels = u.userapp_relations || [];
                    if (rels.length > 0) {
                        matchesOrg = rels.some((r) => r.parent_id === nextOrgId);
                    } else {
                        // Fallback: if we have a resolved organization mapping, use it
                        const orgId = userOrgMap[u.id];
                        matchesOrg = orgId === nextOrgId;
                    }
                }
            }

            return matchesSearch && matchesStatus && matchesUserType && matchesOrg;
        });
        setUserAppsFiltered(res);
    };

    // When an organization is selected, fetch the nearest org for users missing mapping
    useEffect(() => {
        const loadOrgMappings = async () => {
            if (!selectedOrgId || selectedOrgId <= 0) return;
            const missing = userApps
                .filter((u) => u.id !== selectedOrgId && userOrgMap[u.id] === undefined)
                .map((u) => u.id);
            if (missing.length === 0) return;
            try {
                await Promise.all(
                    missing.map(async (uid) => {
                        try {
                            const { data } = await getOrganizationByUserappId(uid);
                            setUserOrgMap((prev) => ({ ...prev, [uid]: data?.id ?? null }));
                        } catch {
                            setUserOrgMap((prev) => ({ ...prev, [uid]: null }));
                        }
                    })
                );
            } catch {
                // ignore batch errors; individual calls handled
            }
        };
        loadOrgMappings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrgId, userApps]);

    // Recompute when org mapping updates (progressively as calls resolve)
    useEffect(() => {
        recomputeFiltered(userApps, searchValue, statusFilter, selectedUserTypes, selectedOrgId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userOrgMap]);

    const onSearch: SearchProps['onSearch'] = (value) => {
        setSearchValue(value);
        recomputeFiltered(userApps, value, statusFilter, selectedUserTypes, selectedOrgId);
    };

    const columns: ColumnsType<TUserApp> = [
        {
            title: t('admin.user.tableHeaders.login'),
            dataIndex: 'login',
            key: 'login',
            render: (text: string, record: TUserApp) =>
                editingKey === record.id ? (
                    <Input
                        value={editingUserApp?.login}
                        onChange={(e) => {
                            if (editingUserApp) {
                                setEditingUserApp({ ...editingUserApp, login: e.target.value });
                            }
                        }}
                    />
                ) : (
                    text
                ),
        },
        {
            title: t('admin.user.tableHeaders.maxSession'),
            dataIndex: 'max_session',
            key: 'max_session',
            render: (text: string, record: TUserApp) =>
                editingKey === record.id ? (
                    <InputNumber
                        value={editingUserApp?.max_session}
                        onChange={(value) => {
                            if (editingUserApp) {
                                setEditingUserApp({ ...editingUserApp, max_session: value ?? -1 });
                            }
                        }}
                    />
                ) : (
                    text
                ),
        },
        {
            title: t('admin.user.tableHeaders.userType'),
            dataIndex: 'usertype_id',
            key: 'usertype_id',
            render: (usertype_id: number, record: TUserApp) =>
                editingKey === record.id ? (
                    <Select
                        value={editingUserApp?.usertype_id}
                        onChange={(value: number) => {
                            if (editingUserApp) {
                                setEditingUserApp({ ...editingUserApp, usertype_id: value });
                            }
                        }}
                    >
                        {userTypes.map((type: TCodification) => (
                            <Select.Option key={type.id} value={type.id}>
                                {type.label}
                            </Select.Option>
                        ))}
                    </Select>
                ) : (
                    userTypes.find((type) => type.id === usertype_id)?.label || 'N/A'
            ),
        },
        {
            title: t('admin.userActivation.tableHeaders.status') || 'Statut',
            dataIndex: 'actif',
            key: 'actif',
            width: 200,
            render: (value: boolean | undefined, record: TUserApp) => (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {canManageActivation ? (
                        <Switch
                            checked={record.actif !== false}
                            onChange={() => toggleActif(record)}
                            checkedChildren={t('admin.userActivation.status.active')}
                            unCheckedChildren={t('admin.userActivation.status.inactive')}
                        />
                    ) : (
                        <Tag color={value === false ? 'red' : 'green'}>
                            {value === false ? t('text.inactive') : t('text.active')}
                        </Tag>
                    )}
                </Space>
            ),
        },
        {
            title: t('admin.user.tableHeaders.actions'),
            key: 'actions',
            fixed: 'right' as 'right',
            width: 220,
            render: (_: any, record: TUserApp) => (
                <Space size="middle">
                    {editingKey === record.id ? (
                        <>
                            <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
                            <Button icon={<StopOutlined />} onClick={cancel} />
                        </>
                    ) : (
                        <>
                            <Button icon={<EditOutlined />} onClick={() => edit(record)} />
                            <Button icon={<KeyOutlined />} onClick={() => showDetails(record)} />
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => confirmDelete(record.id)}
                                loading={deleteId === record.id && deleteLoading}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout 
            title={t('text.users')} 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <Space style={{ marginBottom: 16 }}>
                <Search
                    placeholder={t('text.search')}
                    onSearch={onSearch}
                    onChange={(e) => {
                        const v = e.target.value;
                        setSearchValue(v);
                        recomputeFiltered(userApps, v, statusFilter, selectedUserTypes, selectedOrgId);
                    }}
                    allowClear
                    style={{ width: 200 }}
                />
                <Select
                    value={statusFilter}
                    style={{ width: 200 }}
                    onChange={(v: 'all' | 'active' | 'inactive') => {
                        setStatusFilter(v);
                        recomputeFiltered(userApps, searchValue, v, selectedUserTypes, selectedOrgId);
                    }}
                >
                    <Select.Option value="all">{t('text.all')}</Select.Option>
                    <Select.Option value="active">{t('text.active')}</Select.Option>
                    <Select.Option value="inactive">{t('text.inactive')}</Select.Option>
                </Select>
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: 300 }}
                    placeholder={t('text.organization', 'Organisation')}
                    value={selectedOrgId ?? undefined}
                    onChange={(value: number | undefined) => {
                        const next = typeof value === 'number' ? value : null;
                        setSelectedOrgId(next);
                        recomputeFiltered(userApps, searchValue, statusFilter, selectedUserTypes, next);
                    }}
                    options={useMemo(() => (
                        [{ value: 0, label: t('text.all', 'Tous') } as any].concat(
                            organizations
                                .slice()
                                .sort((a, b) => (a.business_info?.name || a.login).localeCompare(b.business_info?.name || b.login))
                                .map((o) => ({ value: o.id, label: o.business_info?.name || o.login }))
                        )
                    ), [organizations, t])}
                />
                <Select
                    mode="multiple"
                    allowClear
                    style={{ width: 400 }}
                    placeholder={t('text.profil')}
                    value={selectedUserTypes}
                    onChange={(value: number[]) => {
                        setSelectedUserTypes(value);
                        recomputeFiltered(userApps, searchValue, statusFilter, value, selectedOrgId);
                    }}
                >
                    {typeUsers.map((type: TCodification) => (
                        <Select.Option key={type.id} value={type.id}>
                            {type.label}
                        </Select.Option>
                    ))}
                </Select>
                <Button type="primary" onClick={handleAddUserApp}>
                    {t('button.add')}
                </Button>
                {canManageActivation && (
                    <>
                        <Button
                            onClick={() => openBulkDialog('activate')}
                            loading={bulkLoading && bulkAction === 'activate'}
                            disabled={userAppsFiltered.length === 0}
                            type="default"
                        >
                            {t('admin.userActivation.actions.activateAll', 'Activer tout')}
                        </Button>
                        <Button
                            onClick={() => openBulkDialog('deactivate')}
                            loading={bulkLoading && bulkAction === 'deactivate'}
                            disabled={userAppsFiltered.length === 0}
                            danger
                        >
                            {t('admin.userActivation.actions.deactivateAll', 'Désactiver tout')}
                        </Button>
                    </>
                )}
            </Space>
            <Table<TUserApp>
                columns={columns}
                dataSource={userAppsFiltered}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 1000 }}
            />

            <ConfirmationDialog
                visible={isDeleteDialogVisible}
                onConfirm={handleDelete}
                onCancel={() => setIsDeleteDialogVisible(false)}
                loading={deleteLoading}
                title={t('admin.user.deleteDialog.title')}
                description={t('admin.user.deleteDialog.description')}
            />

            <ConfirmationDialog
                visible={isBulkDialogVisible}
                onConfirm={handleBulkApply}
                onCancel={() => setIsBulkDialogVisible(false)}
                loading={bulkLoading}
                confirmText={bulkAction === 'activate' ? 'Activer' : 'Désactiver'}
                cancelText={'Annuler'}
                confirmDanger={bulkAction === 'deactivate'}
                titleText={
                    bulkAction === 'activate'
                        ? "Êtes-vous sûr d'activer tous les utilisateurs affichés ?"
                        : 'Êtes-vous sûr de désactiver tous les utilisateurs affichés ?'
                }
                descriptionText={''}
                title={
                    bulkAction === 'activate'
                        ? t('admin.userActivation.confirm.activateAllTitle', 'Activer les utilisateurs filtrés ?')
                        : t('admin.userActivation.confirm.deactivateAllTitle', 'Désactiver les utilisateurs filtrés ?')
                }
                description={
                    bulkAction === 'activate'
                        ? t('admin.userActivation.confirm.activateAllDesc', 'Cette action activera tous les utilisateurs visibles avec le filtre actuel.')
                        : t('admin.userActivation.confirm.deactivateAllDesc', 'Cette action désactivera tous les utilisateurs visibles avec le filtre actuel.')
                }
            />

            
        </AppLayout>
    );
};

export default UserAppList;
